// Universal converter.
//
// Every format is parsed into a single intermediate model — a *forest* of
// tree nodes — and every format is generated back out of that forest. So a
// conversion is always: parse(from) → forest → generate(to). Adding a format
// means writing one parser and one generator; all N×N pairs come for free.
//
//   Node = {
//     name:     string,        // tag / key / class name
//     attrs:    { k: v },      // attributes (xml/html) or "@"-prefixed keys
//     text:     string | null, // leaf text content
//     children: Node[],        // nested nodes
//   }
//
// The CSS representation mirrors the tree as nested classes and stashes the
// "extra data" (attributes + text) in a comment inside each class — which is
// exactly what makes CSS round-trippable back into XML/HTML.

import yaml from "js-yaml";

/* ============================== formats ============================== */

export const FORMATS = [
  { key: "json", label: "JSON" },
  { key: "xml", label: "XML" },
  { key: "html", label: "HTML" },
  { key: "yml", label: "YAML" },
  { key: "css", label: "CSS" },
  { key: "js", label: "JavaScript" },
];

/* ============================ node helpers =========================== */

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

// Best-effort scalar typing for data targets (json/yaml).
function coerce(s) {
  if (typeof s !== "string") return s;
  const t = s.trim();
  if (t === "true") return true;
  if (t === "false") return false;
  if (t === "null") return null;
  if (/^-?\d+$/.test(t)) {
    const n = parseInt(t, 10);
    if (String(n) === t) return n; // avoid precision loss / leading zeros
  }
  if (/^-?\d*\.\d+$/.test(t)) return parseFloat(t);
  return s;
}

/* ----------------------- value <-> forest ---------------------------- */

// Convert a plain JS value into a node, splitting "@attr" and "#text" keys.
function valueToNode(value, name) {
  if (Array.isArray(value)) {
    return { name, attrs: {}, text: null, children: value.map((v) => valueToNode(v, "item")) };
  }
  if (isObj(value)) {
    const attrs = {};
    let text = null;
    const children = [];
    for (const [k, v] of Object.entries(value)) {
      if (k === "#text") text = String(v);
      else if (k.startsWith("@")) attrs[k.slice(1)] = String(v);
      else if (Array.isArray(v)) for (const item of v) children.push(valueToNode(item, k));
      else children.push(valueToNode(v, k));
    }
    return { name, attrs, text, children };
  }
  return { name, attrs: {}, text: value == null ? "" : String(value), children: [] };
}

function valueToForest(value) {
  if (Array.isArray(value)) return value.map((v) => valueToNode(v, "item"));
  if (isObj(value)) return valueToNode(value, "root").children;
  return [valueToNode(value, "value")];
}

function addKey(obj, key, val) {
  if (key in obj) {
    if (!Array.isArray(obj[key])) obj[key] = [obj[key]];
    obj[key].push(val);
  } else {
    obj[key] = val;
  }
}

function nodeToValue(n) {
  const attrKeys = Object.keys(n.attrs);
  const hasText = n.text != null && n.text !== "";

  if (n.children.length === 0) {
    if (attrKeys.length === 0) return coerce(n.text ?? "");
    const o = {};
    for (const k of attrKeys) o["@" + k] = coerce(n.attrs[k]);
    if (hasText) o["#text"] = coerce(n.text);
    return o;
  }

  const o = {};
  for (const k of attrKeys) o["@" + k] = coerce(n.attrs[k]);
  for (const c of n.children) addKey(o, c.name, nodeToValue(c));
  if (hasText) o["#text"] = coerce(n.text);
  return o;
}

function forestToValue(forest) {
  const o = {};
  for (const n of forest) addKey(o, n.name, nodeToValue(n));
  return o;
}

/* ============================== parsers ============================== */

function parseJson(input) {
  return valueToForest(JSON.parse(input));
}

function parseYml(input) {
  return valueToForest(yaml.load(input));
}

function parseJs(input) {
  let src = input.trim();
  src = src
    .replace(/^\s*(?:export\s+default\s+|(?:const|let|var)\s+[\w$]+\s*=\s*)/, "")
    .replace(/;\s*$/, "");
  // eslint-disable-next-line no-new-func
  const value = new Function(`"use strict"; return (${src});`)();
  return valueToForest(value);
}

function domElementToNode(el) {
  const n = { name: el.nodeName, attrs: {}, text: null, children: [] };
  for (const a of Array.from(el.attributes || [])) n.attrs[a.name] = a.value;
  const elementChildren = Array.from(el.children);
  if (elementChildren.length) {
    for (const c of elementChildren) n.children.push(domElementToNode(c));
    const directText = Array.from(el.childNodes)
      .filter((c) => c.nodeType === 3)
      .map((c) => c.nodeValue.trim())
      .filter(Boolean)
      .join(" ");
    if (directText) n.text = directText;
  } else {
    const t = (el.textContent || "").trim();
    if (t) n.text = t;
  }
  return n;
}

function parseXml(input) {
  const doc = new DOMParser().parseFromString(input, "application/xml");
  const err = doc.querySelector("parsererror");
  if (err) throw new Error(err.textContent.replace(/\s+/g, " ").trim());
  return [domElementToNode(doc.documentElement)];
}

function parseHtml(input) {
  const doc = new DOMParser().parseFromString(input, "text/html");
  const roots = Array.from(doc.body.children).map(domElementToNode);
  return roots.length ? roots : [domElementToNode(doc.documentElement)];
}

/* ----------------------------- CSS parse ----------------------------- */

function parseCss(input) {
  const s = input;
  let i = 0;

  const skipWs = () => {
    while (i < s.length && /\s/.test(s[i])) i++;
  };

  function applyComment(node, raw) {
    const re = /([@#][\w-]+)="([^"]*)"/g;
    let m;
    while ((m = re.exec(raw))) {
      const key = m[1];
      const val = m[2].replace(/&quot;/g, '"');
      if (key === "#text") node.text = val;
      else if (key[0] === "@") node.attrs[key.slice(1)] = val;
    }
  }

  function parseBlock(node) {
    while (i < s.length) {
      skipWs();
      if (i >= s.length || s[i] === "}") {
        i++;
        return;
      }
      if (s[i] === "/" && s[i + 1] === "*") {
        const end = s.indexOf("*/", i);
        applyComment(node, s.slice(i + 2, end < 0 ? s.length : end));
        i = end < 0 ? s.length : end + 2;
        continue;
      }
      const brace = s.indexOf("{", i);
      const semi = s.indexOf(";", i);
      const close = s.indexOf("}", i);
      const nestedFirst =
        brace >= 0 &&
        (semi < 0 || brace < semi) &&
        (close < 0 || brace < close);
      if (nestedFirst) {
        const selector = s.slice(i, brace).trim();
        i = brace + 1;
        const child = { name: selectorName(selector), attrs: {}, text: null, children: [] };
        parseBlock(child);
        node.children.push(child);
      } else {
        // declaration we don't model — skip to ; or }
        const end =
          semi >= 0 && (close < 0 || semi < close) ? semi + 1 : close >= 0 ? close : s.length;
        i = end;
      }
    }
  }

  const forest = [];
  while (i < s.length) {
    skipWs();
    if (i >= s.length) break;
    if (s[i] === "/" && s[i + 1] === "*") {
      const end = s.indexOf("*/", i);
      i = end < 0 ? s.length : end + 2;
      continue;
    }
    const brace = s.indexOf("{", i);
    if (brace < 0) break;
    const selector = s.slice(i, brace).trim();
    i = brace + 1;
    const node = { name: selectorName(selector), attrs: {}, text: null, children: [] };
    parseBlock(node);
    forest.push(node);
  }
  return forest;
}

function selectorName(selector) {
  // take the last token, drop a leading "." and combinators
  const token = selector.split(/[\s>+~]+/).filter(Boolean).pop() || "node";
  return token.replace(/^\./, "").replace(/\\/g, "") || "node";
}

/* ============================= generators ============================ */

const escText = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escAttr = (s) => escText(s).replace(/"/g, "&quot;");

const VOID = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input",
  "link", "meta", "param", "source", "track", "wbr",
]);

function renderMarkup(forest, { html = false } = {}) {
  return forest.map((n) => renderEl(n, 0, html)).join("\n");
}

function renderEl(n, depth, html) {
  const pad = "  ".repeat(depth);
  const name = html ? n.name.toLowerCase() : n.name;
  const attrs = Object.entries(n.attrs)
    .map(([k, v]) => ` ${k}="${escAttr(v)}"`)
    .join("");
  const hasKids = n.children.length > 0;
  const hasText = n.text != null && n.text !== "";

  if (!hasKids && !hasText) {
    if (html && VOID.has(name)) return `${pad}<${name}${attrs}>`;
    return `${pad}<${name}${attrs}/>`;
  }
  if (!hasKids && hasText) {
    return `${pad}<${name}${attrs}>${escText(n.text)}</${name}>`;
  }
  const childLines = n.children.map((c) => renderEl(c, depth + 1, html)).join("\n");
  const textLine = hasText ? `${"  ".repeat(depth + 1)}${escText(n.text)}\n` : "";
  return `${pad}<${name}${attrs}>\n${textLine}${childLines}\n${pad}</${name}>`;
}

const escComment = (s) => String(s).replace(/\*\//g, "* /").replace(/"/g, "&quot;");
const cssIdent = (s) => String(s).replace(/[^a-zA-Z0-9_-]/g, "-") || "node";

function renderCss(forest) {
  return forest.map((n) => renderCssRule(n, 0)).join("\n\n");
}

function renderCssRule(n, depth) {
  const pad = "  ".repeat(depth);
  const innerPad = "  ".repeat(depth + 1);
  const parts = Object.entries(n.attrs).map(([k, v]) => `@${k}="${escComment(v)}"`);
  if (n.text != null && n.text !== "") parts.push(`#text="${escComment(n.text)}"`);

  const body = [];
  if (parts.length) body.push(`${innerPad}/* ${parts.join(" ")} */`);
  for (const c of n.children) body.push(renderCssRule(c, depth + 1));

  if (body.length === 0) return `${pad}.${cssIdent(n.name)} {}`;
  return `${pad}.${cssIdent(n.name)} {\n${body.join("\n")}\n${pad}}`;
}

function genJson(forest) {
  return JSON.stringify(forestToValue(forest), null, 2);
}
function genYml(forest) {
  return yaml.dump(forestToValue(forest), { lineWidth: 100 }).trimEnd();
}
function genJs(forest) {
  return `const data = ${JSON.stringify(forestToValue(forest), null, 2)};`;
}
function genXml(forest) {
  return renderMarkup(forest, { html: false });
}
function genHtml(forest) {
  return renderMarkup(forest, { html: true });
}
function genCss(forest) {
  return renderCss(forest);
}

/* ============================== registry ============================= */

const parsers = {
  json: parseJson,
  yml: parseYml,
  js: parseJs,
  xml: parseXml,
  html: parseHtml,
  css: parseCss,
};

const generators = {
  json: genJson,
  yml: genYml,
  js: genJs,
  xml: genXml,
  html: genHtml,
  css: genCss,
};

export function canConvert(from, to) {
  return from !== to && !!parsers[from] && !!generators[to];
}

export function convert(input, from = "json", to = "xml") {
  try {
    if (!input || !input.trim()) return "";
    if (!parsers[from]) throw new Error(`Cannot read "${from}"`);
    if (!generators[to]) throw new Error(`Cannot write "${to}"`);
    return generators[to](parsers[from](input));
  } catch (err) {
    return `/* Conversion error: ${err.message} */`;
  }
}
